import {
  ReactNode,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { When } from "react-if";
import { DateTime } from "luxon";
import {
  ActivityType,
  CallFollowUpTypeLabels,
  CallFollowUpType,
  ITeamActivityContactRow,
  ITeamActivityUserResponse,
  ITeamFeedItem,
  PaymentPromiseStatus,
  PolicyEscalationType,
  TeamActivityPeriodLabels,
  TeamFeedItemType,
} from "shared";

import { Badge } from "../../../common/Atoms/Badge";
import { Button } from "../../../common/Atoms/Button";
import { Select } from "../../../common/Atoms/Select";
import { classNames } from "../../../common/lib/classNames";
import { formatAmount } from "../../../common/lib/helpers";
import { useFetchTeamActivityUserQuery } from "../../services/api/teamActivityApi/teamActivity";
import {
  formatCount,
  rate,
  toCsv,
} from "../EmailPerformance/stats";
import { LoadingOverlay } from "../Molecules/LoadingOverlay";
import { WrappedLink } from "../WrappedLink";

import {
  cardClass,
  CardHeading,
  ContactLink,
  ErrorState,
  FollowUpsCard,
  initials,
  LegendItem,
  linkClass,
  outcomeColor,
  OutcomeBars,
  PeriodSelect,
  SegmentedToggle,
  TYPE_COLORS,
  TypeLegend,
  usePeriod,
  userPath,
  WeeklyChart,
} from "./common";
import {
  activities,
  daysSince,
  dateRangeLabel,
  formatTalkTime,
  inEscalationRate,
  keptRate,
  pct,
  reachedRate,
  relativeTime,
} from "./stats";

const thClass = `px-3 py-2.5 font-semibold`;
const ESCALATION_TYPES: Record<string, string> = {
  [PolicyEscalationType.CALL]: `Call`,
  [PolicyEscalationType.STOP_CREDIT]: `Stop Credit`,
  [PolicyEscalationType.LETTER]: `Letter`,
  [PolicyEscalationType.OTHER]: `Other`,
};
const PROMISE_BADGES: Record<PaymentPromiseStatus, `blue` | `green` | `red` | `gray`> = {
  [PaymentPromiseStatus.OPEN]: `blue`,
  [PaymentPromiseStatus.KEPT]: `green`,
  [PaymentPromiseStatus.BROKEN]: `red`,
  [PaymentPromiseStatus.CANCELLED]: `gray`,
};

type ContactFilter = `all` | `overdue` | `idle`;

const isIdle = (c: ITeamActivityContactRow) => {
  const days = daysSince(c.lastContactAt);

  return days === null || days > 30;
};

const CONTACT_FILTERS: Record<ContactFilter, (c: ITeamActivityContactRow) => boolean> = {
  all: () => true,
  overdue: c => c.overdueHomeCents > 0,
  idle: isIdle,
};

function Stat({ label, value, sub, valueClass }: { label: string; value: string; sub?: string; valueClass?: string }) {
  return (
    <div className={ `flex flex-col gap-0.5` }>
      <span className={ `text-[13px] text-gray-500` }>{ label }</span>
      <span className={ classNames(`text-[22px] font-semibold tabular-nums text-gray-900`, valueClass) }>{ value }</span>
      <When condition={ !!sub }>
        <span className={ `text-xs text-gray-500` }>{ sub }</span>
      </When>
    </div>
  );
}

/** A segmented bar with a legend row under it for each segment */
function StackedBar({ parts }: { parts: Array<{ label: string; value: number; display: string; color: string }> }) {
  const total = parts.reduce((sum, p) => sum + p.value, 0);

  return (
    <>
      <div className={ `flex h-2.5 gap-0.5 overflow-hidden rounded bg-gray-100` }>
        { parts.map(p => (
          <div key={ p.label }
            style={ { width: `${total > 0 ? (p.value / total) * 100 : 0}%`, background: p.color } } />
        )) }
      </div>
      { parts.map(p => (
        <div key={ p.label }
          className={ `flex items-center justify-between text-[13px]` }>
          <LegendItem color={ p.color }
            label={ p.label } />
          <span className={ `font-medium tabular-nums` }>{ p.display }</span>
        </div>
      )) }
    </>
  );
}

function feedDot(item: ITeamFeedItem) {
  if (item.type === TeamFeedItemType.ESCALATION_CLOSED) {
    return TYPE_COLORS.emails;
  }

  if (item.type === TeamFeedItemType.NOTE || item.activityType === ActivityType.TEXT_MESSAGE) {
    return TYPE_COLORS.texts;
  }

  if (item.activityType === ActivityType.EXTERNAL_EMAIL) {
    return TYPE_COLORS.emails;
  }

  return outcomeColor(item.callOutcome);
}

function feedWhen(item: ITeamFeedItem) {
  const seconds = item.durationSeconds;

  return [
    DateTime.fromISO(item.at).toFormat(`d LLL, h:mma`),
    seconds ? `${Math.floor(seconds / 60)}m ${seconds % 60}s` : ``,
    item.clickToCall ? `click to call` : ``,
  ].filter(Boolean).join(` · `);
}

function ContactsTable({ rows, homeCurrency }: { rows: ITeamActivityContactRow[]; homeCurrency: string }) {
  return (
    <div className={ `mt-3 max-h-[640px] overflow-auto` }>
      <table className={ `w-full min-w-[760px] border-collapse text-sm` }>
        <thead className={ `sticky top-0` }>
          <tr className={ `border-b border-gray-200 bg-gray-50 text-left` }>
            <th className={ thClass }>{ `Contact` }</th>
            <th className={ `${thClass} text-right` }>{ `Overdue` }</th>
            <th className={ `${thClass} text-right` }>{ `Oldest overdue` }</th>
            <th className={ thClass }>{ `Last contact` }</th>
            <th className={ thClass }>{ `Escalation` }</th>
            <th className={ thClass }>{ `Promise` }</th>
          </tr>
        </thead>
        <tbody>
          { rows.map(c => {
            const idle = isIdle(c);
            const esc = c.escalation;

            return (
              <tr key={ c.contactId }
                className={ `border-b border-gray-200` }>
                <td className={ `px-3 py-2.5` }><ContactLink id={ c.contactId }
                  name={ c.name } /></td>
                <td className={ `px-3 py-2.5 text-right tabular-nums text-gray-900` }>{ formatAmount(c.overdueHomeCents, homeCurrency) }</td>
                <td className={ classNames(`px-3 py-2.5 text-right tabular-nums`, (c.oldestOverdueDays ?? 0) > 60 ? `text-paidniceRed-700` : `text-gray-700`) }>
                  { c.oldestOverdueDays === null ? `-` : `${c.oldestOverdueDays} days` }
                </td>
                <td className={ `px-3 py-2.5` }>
                  <div className={ idle ? `font-semibold text-paidniceRed-700` : `text-gray-700` }>{ relativeTime(c.lastContactAt) }</div>
                  <div className={ `text-xs text-gray-500` }>{ c.lastContactHow ?? `No activity or notes` }</div>
                </td>
                <td className={ `px-3 py-2.5 text-gray-700` }>
                  { esc ? (
                    <>
                      <div>{ ESCALATION_TYPES[esc.type] ?? esc.type }</div>
                      <div className={ classNames(`text-xs`, esc.pastScheduledDays !== null ? `text-paidniceRed-700` : `text-gray-500`) }>
                        { esc.pastScheduledDays !== null
                          ? `Past scheduled ${esc.pastScheduledDays} ${esc.pastScheduledDays === 1 ? `day` : `days`}`
                          : `Scheduled ${DateTime.fromISO(esc.scheduledDate).toFormat(`d LLL`)}` }
                      </div>
                    </>
                  ) : `-` }
                </td>
                <td className={ `px-3 py-2.5` }>
                  { c.promiseStatus
                    ? <Badge color={ PROMISE_BADGES[c.promiseStatus] }
                      message={ c.promiseStatus.charAt(0).toUpperCase() + c.promiseStatus.slice(1) }
                      size={ `sm` } />
                    : <span className={ `text-gray-400` }>{ `-` }</span> }
                </td>
              </tr>
            );
          }) }
        </tbody>
      </table>
    </div>
  );
}

function downloadContactsCsv(data: ITeamActivityUserResponse, rows: ITeamActivityContactRow[], period: string) {
  const csv = toCsv([
    [
      `Contact`,
      `Overdue (${data.homeCurrency})`,
      `Oldest overdue (days)`,
      `Last contact`,
      `Last contact how`,
      `Escalation`,
      `Escalation scheduled`,
      `Days past scheduled`,
      `Promise`,
    ],
    ...rows.map(c => [
      c.name,
      (c.overdueHomeCents / 100).toFixed(2),
      c.oldestOverdueDays,
      c.lastContactAt ? DateTime.fromISO(c.lastContactAt).toFormat(`yyyy-MM-dd`) : ``,
      c.lastContactHow,
      c.escalation ? (ESCALATION_TYPES[c.escalation.type] ?? c.escalation.type) : ``,
      c.escalation?.scheduledDate ?? ``,
      c.escalation?.pastScheduledDays ?? null,
      c.promiseStatus,
    ]),
  ]);
  const url = URL.createObjectURL(new Blob([csv], { type: `text/csv` }));
  const a = document.createElement(`a`);
  a.href = url;
  a.download = `team-activity-${data.user.name.toLowerCase().replace(/[^a-z0-9]+/g, `-`)}-${period}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Kpis({ data }: { data: ITeamActivityUserResponse }) {
  const { counts: c, teamTotals: t, escalations: esc } = data;
  const active = Math.max(data.activeUsers, 1);
  const reach = reachedRate(c);
  const teamReach = reachedRate(t);
  const kept = keptRate(c);
  const teamKept = keptRate(t);
  const below = (value: number | null, team: number | null) => value !== null && team !== null && value < team - 5;
  const kpis: Array<{ label: string; value: string; sub: string; valueClass?: string }> = [
    { label: `Activities logged`, value: formatCount(activities(c)), sub: `Team average ${formatCount(Math.round(activities(t) / active))}` },
    { label: `Phone calls`, value: formatCount(c.phoneCalls), sub: `${formatTalkTime(c.talkTimeSeconds)} talk time` },
    {
      label: `Customer reached on calls`,
      value: pct(reach),
      sub: `Team ${pct(teamReach)}`,
      valueClass: below(reach, teamReach) ? `text-paidniceRed-700` : undefined,
    },
    { label: `Promised to pay`, value: formatAmount(c.promisedHomeCents, data.homeCurrency), sub: `${formatCount(c.promises)} promises` },
    {
      label: `Promises kept`,
      value: pct(kept),
      sub: `Team ${pct(teamKept)} · ${formatCount(c.promisesBroken)} broken`,
      valueClass: below(kept, teamKept) ? `text-amber-700` : undefined,
    },
    {
      label: `Escalations past scheduled`,
      value: formatCount(esc.pastScheduled),
      sub: `of ${formatCount(esc.open)} open`,
      valueClass: esc.pastScheduled > 0 ? `text-paidniceRed-700` : undefined,
    },
  ];

  return (
    <div className={ `grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6` }>
      { kpis.map(k => (
        <div key={ k.label }
          className={ `flex flex-col gap-1.5 rounded-lg bg-white px-4 py-4 shadow-xs` }>
          <div className={ `text-[13px] text-gray-500` }>{ k.label }</div>
          <div className={ classNames(`text-2xl font-semibold leading-tight tabular-nums text-gray-900`, k.valueClass) }>{ k.value }</div>
          <div className={ `text-xs text-gray-500` }>{ k.sub }</div>
        </div>
      )) }
    </div>
  );
}

function RecordedFromCalls({ data }: { data: ITeamActivityUserResponse }) {
  const c = data.counts;
  const money = (cents: number) => formatAmount(cents, data.homeCurrency);
  const items: Array<{ type: CallFollowUpType; color: `blue` | `orange` | `purple` | `indigo`; n: number; detail: string }> = [
    {
      type: CallFollowUpType.PAYMENT_PROMISE,
      color: `blue`,
      n: c.promises,
      detail: `${money(c.promisedHomeCents)} · ${c.promisesKept} kept, ${c.promisesBroken} broken, ${c.promisesOpen} open, ${c.promisesCancelled} cancelled`,
    },
    {
      type: CallFollowUpType.INVOICE_QUERY,
      color: `orange`,
      n: c.queries,
      detail: `${c.queriesResolved} resolved, ${c.queries - c.queriesResolved} open`,
    },
    {
      type: CallFollowUpType.PAYMENT_PLAN,
      color: `purple`,
      n: c.plans,
      detail: `${money(c.planHomeCents)} across ${c.plans} ${c.plans === 1 ? `plan` : `plans`}`,
    },
    {
      type: CallFollowUpType.FOLLOW_UP_EMAIL,
      color: `indigo`,
      n: c.followUpEmails,
      detail: `${c.followUpEmailsSent} sent, ${c.followUpEmailsFailed} failed`,
    },
  ];

  return (
    <div className={ `${cardClass} flex flex-col gap-3.5` }>
      <CardHeading title={ `Recorded from calls` } />
      { items.map(item => (
        <div key={ item.type }
          className={ `flex flex-col gap-1.5 border-b border-gray-100 pb-3 last:border-b-0` }>
          <div className={ `flex items-center justify-between` }>
            <Badge color={ item.color }
              message={ CallFollowUpTypeLabels[item.type] }
              size={ `sm` } />
            <span className={ `text-xl font-semibold tabular-nums` }>{ formatCount(item.n) }</span>
          </div>
          <div className={ `text-[13px] text-gray-500` }>{ item.detail }</div>
        </div>
      )) }
    </div>
  );
}

function EscalationsCard({ data }: { data: ITeamActivityUserResponse }) {
  const { escalations: e, teamEscalations: te } = data;
  const days = (v: number | null) => (v === null ? `-` : `${v.toFixed(1)} days`);
  const pastShare = rate(e.pastScheduled, e.open);
  const teamPastShare = rate(te.pastScheduled, te.open);

  return (
    <div className={ `${cardClass} flex flex-col gap-4` }>
      <CardHeading title={ `Assigned escalations` }
        action={ <WrappedLink to={ `/escalations` }
          className={ `text-sm font-medium underline` }>{ `Open in Escalations` }</WrappedLink> } />
      <div className={ `grid grid-cols-2 gap-x-5 gap-y-4` }>
        <Stat label={ `Open` }
          value={ formatCount(e.open) }
          sub={ `Team ${formatCount(te.open)}` } />
        <Stat label={ `Past scheduled date` }
          value={ formatCount(e.pastScheduled) }
          sub={ `${pct(pastShare)} of open, team ${pct(teamPastShare)}` }
          valueClass={ `text-paidniceRed-700` } />
        <Stat label={ `Closed` }
          value={ formatCount(e.closed) }
          sub={ TeamActivityPeriodLabels[data.period] } />
        <Stat label={ `Median time to close` }
          value={ days(e.medianDaysToClose) }
          sub={ `Team ${days(te.medianDaysToClose)}` } />
      </div>
      <div className={ `flex flex-col gap-2 border-t border-gray-100 pt-3.5` }>
        <div className={ `text-sm font-medium text-gray-900` }>{ `${formatCount(e.closed)} closed, by how they were worked` }</div>
        <StackedBar parts={ [
          { label: `Activity logged`, value: e.closedWithActivity, display: formatCount(e.closedWithActivity), color: `#2b495a` },
          { label: `Notes only, no activity`, value: e.closedNotesOnly, display: formatCount(e.closedNotesOnly), color: `#6196b3` },
          { label: `Nothing logged`, value: e.closedNothingLogged, display: formatCount(e.closedNothingLogged), color: `#d1d5db` },
        ] } />
      </div>
    </div>
  );
}

function BalanceCard({ data }: { data: ITeamActivityUserResponse }) {
  const { portfolio: p, teamPortfolio: tp, homeCurrency: cur } = data;
  const asAt = DateTime.fromISO(data.end).toFormat(`d LLL yyyy`);
  const overduePct = rate(p.overdueHomeCents, p.outstandingHomeCents);
  const teamOverduePct = rate(tp.overdueHomeCents, tp.outstandingHomeCents);
  const aging = p.aging;

  return (
    <div className={ `${cardClass} flex flex-col gap-4` }>
      <CardHeading title={ `Assigned balance` }
        subtitle={ `${formatCount(p.contacts)} contacts · ${formatCount(p.openInvoices)} open invoices · as at ${asAt}` } />
      <div className={ `grid grid-cols-2 gap-x-5 gap-y-4` }>
        <Stat label={ `Outstanding` }
          value={ formatAmount(p.outstandingHomeCents, cur) }
          sub={ `${formatCount(p.openInvoices)} invoices` } />
        <Stat label={ `Overdue` }
          value={ formatAmount(p.overdueHomeCents, cur) }
          sub={ `${pct(overduePct)} of outstanding, team ${pct(teamOverduePct)}` }
          valueClass={ `text-paidniceRed-700` } />
        <Stat label={ `Collected` }
          value={ formatAmount(p.collectedHomeCents, cur) }
          sub={ TeamActivityPeriodLabels[data.period] } />
        <Stat label={ `No contact in 30 days` }
          value={ formatCount(p.noContactIn30Days) }
          sub={ `of ${formatCount(p.contacts)} contacts` }
          valueClass={ `text-paidniceRed-700` } />
      </div>
      <div className={ `flex flex-col gap-2 border-t border-gray-100 pt-3.5` }>
        <div className={ `text-sm font-medium text-gray-900` }>{ `Overdue by days past due` }</div>
        <StackedBar parts={ [
          { label: `1 to 30 days`, value: aging.days1to30, display: formatAmount(aging.days1to30, cur), color: `#8ab1c7` },
          { label: `31 to 60 days`, value: aging.days31to60, display: formatAmount(aging.days31to60, cur), color: `#467691` },
          { label: `61 to 90 days`, value: aging.days61to90, display: formatAmount(aging.days61to90, cur), color: `#ff6566` },
          { label: `90+ days`, value: aging.days90plus, display: formatAmount(aging.days90plus, cur), color: `#c11415` },
        ] } />
      </div>
    </div>
  );
}

function AssignedContacts({ data }: { data: ITeamActivityUserResponse }) {
  const [filter, setFilter] = useState<ContactFilter>(`overdue`);
  const counts = useMemo(() => ({
    all: data.contacts.length,
    overdue: data.contacts.filter(CONTACT_FILTERS.overdue).length,
    idle: data.contacts.filter(CONTACT_FILTERS.idle).length,
  }), [data.contacts]);
  const rows = useMemo(() => data.contacts.filter(CONTACT_FILTERS[filter]), [data.contacts, filter]);

  return (
    <div className={ `${cardClass} min-w-0 lg:col-span-2` }>
      <CardHeading
        title={ `Assigned contacts` }
        subtitle={ `Largest overdue balance first. Last contact counts activities and notes.${data.portfolio.contacts > data.contacts.length ? ` Showing the ${data.contacts.length} largest of ${data.portfolio.contacts}.` : ``}` }
        action={
          <SegmentedToggle<ContactFilter>
            options={ [
              { value: `all`, label: `All ${counts.all}` },
              { value: `overdue`, label: `Overdue ${counts.overdue}` },
              { value: `idle`, label: `No contact in 30 days ${counts.idle}` },
            ] }
            value={ filter }
            onChange={ setFilter } />
        } />
      <When condition={ rows.length > 0 }>
        <ContactsTable rows={ rows }
          homeCurrency={ data.homeCurrency } />
      </When>
      <When condition={ rows.length === 0 }>
        <div className={ `py-10 text-center text-sm text-gray-500` }>{ `No contacts match this filter.` }</div>
      </When>
    </div>
  );
}

function Feed({ feed }: { feed: ITeamFeedItem[] }) {
  return (
    <div className={ `${cardClass} flex flex-col gap-1` }>
      <CardHeading title={ `Recent activity and notes` }
        action={ <WrappedLink to={ `/calls` }
          className={ `text-sm font-medium underline` }>{ `View calls` }</WrappedLink> } />
      <When condition={ feed.length === 0 }>
        <div className={ `py-6 text-sm text-gray-500` }>{ `Nothing logged yet.` }</div>
      </When>
      { feed.map(item => (
        <div key={ `${item.type}-${item.contactId}-${item.at}` }
          className={ `flex gap-3 border-b border-gray-100 py-2.5 last:border-b-0` }>
          <span className={ `mt-1.5 h-2 w-2 shrink-0 rounded-full` }
            style={ { background: feedDot(item) } } />
          <div className={ `flex min-w-0 flex-col gap-0.5` }>
            <div className={ `text-sm text-gray-900` }>
              <span className={ `font-medium` }>{ item.title }</span>
              { ` · ` }
              <WrappedLink to={ `/contacts/${item.contactId}` }
                className={ linkClass }>{ item.contactName }</WrappedLink>
            </div>
            <When condition={ !!item.detail }>
              <div className={ `text-[13px] text-gray-600` }>{ item.detail }</div>
            </When>
            <div className={ `text-xs text-gray-400` }>{ feedWhen(item) }</div>
          </div>
        </div>
      )) }
    </div>
  );
}

function Header({ data, userId, onExport }: { data?: ITeamActivityUserResponse; userId: string; onExport: () => void }) {
  const { organisationId } = useParams<{ organisationId: string }>();
  const navigate = useNavigate();
  const { period, setPeriod } = usePeriod();
  const userOptions = (data?.users ?? []).map(u => ({ label: u.name, value: u.id }));
  const name = data?.user.name ?? ``;
  const crumb: ReactNode = (
    <div className={ `flex items-center gap-2 text-sm text-gray-500` }>
      <WrappedLink to={ `/team-activity?period=${period}` }
        className={ `font-medium underline` }>{ `Team Activity` }</WrappedLink>
      <span>{ `/` }</span>
      <span className={ `font-medium text-gray-900` }>{ name }</span>
    </div>
  );

  return (
    <div className={ `flex flex-col gap-3.5` }>
      { crumb }
      <div className={ `flex flex-wrap items-center justify-between gap-4` }>
        <div className={ `flex items-center gap-3.5` }>
          <span className={ `flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#467691] text-[17px] font-semibold text-white` }>
            { initials(name) }
          </span>
          <div>
            <h2 className={ `text-xl font-semibold text-gray-900` }>{ name }</h2>
            <div className={ `text-sm text-gray-500` }>
              { data ? `${data.user.email} · ${dateRangeLabel(data.start, data.end)}` : `` }
            </div>
          </div>
        </div>
        <div className={ `flex flex-wrap items-center gap-2` }>
          <div className={ `w-44` }>
            <Select
              options={ userOptions }
              selected={ userId }
              onChange={ ({ value }) => navigate(`/${organisationId}${userPath(String(value), period)}`) }
              size={ `sm` }
            />
          </div>
          <PeriodSelect period={ period }
            onChange={ setPeriod } />
          <Button color={ `gray` }
            onClick={ onExport }
            disabled={ !data || data.contacts.length === 0 }>
            { `Export CSV` }
          </Button>
        </div>
      </div>
    </div>
  );
}

export function TeamActivityUser() {
  const { organisationId, userId } = useParams<{ organisationId: string; userId: string }>();
  const { period } = usePeriod();

  const { data, isLoading, isFetching, isError, refetch } = useFetchTeamActivityUserQuery({ organisationId: organisationId!, userId: userId!, period }, {
    skip: !organisationId || !userId,
  });

  const exportCsv = () => {
    if (data) {
      downloadContactsCsv(data, data.contacts, period);
    }
  };

  return (
    <LoadingOverlay loading={ isLoading || isFetching }>
      <div className={ `mx-auto flex max-w-[1400px] flex-col gap-5` }>
        <Header data={ data }
          userId={ userId! }
          onExport={ exportCsv } />

        <When condition={ isError && !isFetching }>
          <ErrorState what={ `this user's activity` }
            onRetry={ refetch } />
        </When>

        <When condition={ !!data }>
          { data && (
            <>
              <Kpis data={ data } />

              <div className={ `grid items-stretch gap-5 lg:grid-cols-3` }>
                <div className={ `${cardClass} flex flex-col gap-3 lg:col-span-2` }>
                  <div className={ `flex flex-wrap items-baseline justify-between gap-2` }>
                    <h3 className={ `text-base font-semibold text-gray-900` }>{ `Activities per week` }</h3>
                    <div className={ `flex flex-wrap gap-3.5` }>
                      <TypeLegend />
                      <LegendItem color={ `` }
                        label={ `Team average` }
                        dashed />
                    </div>
                  </div>
                  <WeeklyChart weeks={ data.weeks }
                    mode={ `type` }
                    average={ data.teamWeeklyAverage } />
                  <div className={ `text-[13px] text-gray-500` }>
                    { `${formatCount(data.counts.inEscalations)} of ${formatCount(activities(data.counts))} activities (${pct(inEscalationRate(data.counts))}) were logged against an escalation. Team: ${pct(inEscalationRate(data.teamTotals))}.` }
                  </div>
                </div>
                <div className={ `${cardClass} flex flex-col gap-3` }>
                  <CardHeading title={ `Call outcomes` }
                    subtitle={ `${formatCount(data.counts.phoneCalls)} calls · team share in grey` } />
                  <OutcomeBars counts={ data.counts }
                    teamCounts={ data.teamTotals } />
                </div>
              </div>

              <div className={ `grid items-stretch gap-5 lg:grid-cols-3` }>
                <RecordedFromCalls data={ data } />
                <EscalationsCard data={ data } />
                <BalanceCard data={ data } />
              </div>

              <FollowUpsCard followUps={ data.followUps }
                homeCurrency={ data.homeCurrency }
                showUser={ false }
                forUser={ data.user.name } />

              <div className={ `grid items-start gap-5 lg:grid-cols-3` }>
                <AssignedContacts data={ data } />
                <Feed feed={ data.feed } />
              </div>
            </>
          ) }
        </When>
      </div>
    </LoadingOverlay>
  );
}
